import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

const KEYS = ["caller_name", "caller_phone", "location_raw", "call_type_id", "priority"];

export function useAutosave(incident) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ caller_name: "", caller_phone: "", location_raw: "", call_type_id: "", priority: 3 });
  const [saveState, setSaveState] = useState("saved");
  const dirty = useRef(new Set());
  const formRef = useRef(form);
  const timer = useRef(null);
  const idRef = useRef(incident?.id);
  formRef.current = form;

  useEffect(() => {
    if (incident?.id !== idRef.current) { dirty.current.clear(); idRef.current = incident?.id; setSaveState("saved"); }
    if (!incident) return;
    setForm((f) => {
      const n = { ...f };
      KEYS.forEach((k) => { if (!dirty.current.has(k)) n[k] = incident[k] ?? (k === "priority" ? 3 : ""); });
      return n;
    });
  }, [incident]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    if (!idRef.current || dirty.current.size === 0) return;
    const keys = [...dirty.current];
    const payload = {};
    keys.forEach((k) => { payload[k] = formRef.current[k]; });
    if ("priority" in payload) payload.priority = Number(payload.priority);
    dirty.current.clear();
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/incidents/${idRef.current}`, payload);
      qc.setQueryData(["incident", idRef.current], data);
      qc.invalidateQueries({ queryKey: ["board"] });
      setSaveState("saved");
    } catch {
      keys.forEach((k) => dirty.current.add(k));
      setSaveState("error");
    }
  }, [qc]);

  const update = useCallback((key, value, immediate = false) => {
    setForm((f) => ({ ...f, [key]: value }));
    dirty.current.add(key);
    setSaveState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, immediate ? 0 : 600);
  }, [flush]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return { form, update, flush, saveState };
}
